import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import n8n from "../../assets/n8n.png";
import dialogflow from "../../assets/dialogflow.png";
import webhooks from "../../assets/webhook.png";
import typebot from "../../assets/typebot.jpg";
import flowbuilder from "../../assets/flowbuilders.png";

import { makeStyles } from "@material-ui/core/styles";

import {
  Avatar,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import { DeleteOutline, Edit, Add, DeviceHub } from "@material-ui/icons";

import SearchIcon from "@material-ui/icons/Search";

import MainHeader from "../../components/MainHeader";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import IntegrationModal from "../../components/QueueIntegrationModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
  if (action.type === "LOAD_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const newIntegrations = [];

    queueIntegration.forEach((integration) => {
      const integrationIndex = state.findIndex((u) => u.id === integration.id);
      if (integrationIndex !== -1) {
        state[integrationIndex] = integration;
      } else {
        newIntegrations.push(integration);
      }
    });

    return [...state, ...newIntegrations];
  }

  if (action.type === "UPDATE_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const integrationIndex = state.findIndex((u) => u.id === queueIntegration.id);

    if (integrationIndex !== -1) {
      state[integrationIndex] = queueIntegration;
      return [...state];
    }
    return [queueIntegration, ...state];
  }

  if (action.type === "DELETE_INTEGRATION") {
    const integrationId = action.payload;

    const integrationIndex = state.findIndex((u) => u.id === integrationId);
    if (integrationIndex !== -1) {
      state.splice(integrationIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: "1 1 auto",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      overflowY: "auto",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch"
    }
  },
  pageHeader: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 16,
    color: "#1e2a44",
    background: "#EDF4FF",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  pageHeaderTitle: {
    fontWeight: 600,
    letterSpacing: 0.1,
    fontSize: "1.2rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.05rem",
    },
  },
  pageHeaderSubtitle: {
    marginTop: theme.spacing(0.25),
    color: "rgba(30,42,68,0.78)",
    fontSize: "0.8rem",
    lineHeight: 1.35,
  },
  headerChip: {
    backgroundColor: "#EAF1FF",
    color: "#2f4b7c",
    border: "1px solid #d7e5ff",
    fontWeight: 500,
    fontSize: "0.72rem",
    height: 24,
  },
  controlsPaper: {
    marginBottom: theme.spacing(2),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
    padding: theme.spacing(1.25),
    backgroundColor: theme.palette.background.paper,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.default,
    },
    "& .MuiInputBase-input": {
      fontSize: "0.82rem",
      paddingTop: 13,
      paddingBottom: 13,
    },
  },
  actionButton: {
    minHeight: 42,
    borderRadius: 10,
    fontWeight: 600,
    fontSize: "0.75rem",
    padding: theme.spacing(0.8, 1.4),
    boxShadow: "0 6px 14px rgba(7, 64, 171, 0.14)",
  },
  mainPaper: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 12px 26px rgba(17, 24, 39, 0.09)",
  },
  tableHeaderCell: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(15, 23, 42, 0.03)",
    fontSize: "0.76rem",
  },
  tableRow: {
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.03)"
          : "rgba(15, 23, 42, 0.035)",
    },
  },
  tableCellText: {
    fontSize: "0.78rem",
  },
  avatar: {
    width: "110px",
    height: "32px",
    borderRadius: 4,
  },
  actionCell: {
    minWidth: 120,
  },
  actionIconButton: {
    width: 32,
    height: 32,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(0, 0.25),

    "& svg": {
      fontSize: 17
    }
  },

  desktopOnly: {
    display: "block",
    width: "100%",

    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },

  mobileOnly: {
    display: "none",

    [theme.breakpoints.down("sm")]: {
      display: "block",
      width: "100%"
    }
  },

  integrationCards: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },

  integrationCard: {
    width: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(1.5),
    borderRadius: 16,
    background:
      theme.palette.type === "dark"
        ? "rgba(15,23,42,.94)"
        : "#FFFFFF",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 10px 28px rgba(0,0,0,.24)"
        : "0 8px 22px rgba(15,23,42,.055)"
  },

  integrationCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.2),
    minWidth: 0
  },

  integrationLogoWrap: {
    width: 52,
    height: 52,
    flexShrink: 0,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.05)"
        : "#F8FAFC",
    border: `1px solid ${theme.palette.divider}`
  },

  mobileAvatar: {
    width: "100%",
    height: "100%",
    borderRadius: 0,
    objectFit: "contain"
  },

  integrationIdentity: {
    flex: 1,
    minWidth: 0
  },

  integrationName: {
    color: theme.palette.text.primary,
    fontSize: ".93rem",
    fontWeight: 850,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  integrationType: {
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: ".66rem",
    fontWeight: 750,
    textTransform: "uppercase",
    letterSpacing: ".04em"
  },

  integrationActions: {
    display: "flex",
    gap: theme.spacing(.6),
    flexShrink: 0
  },

  cardActionButton: {
    width: 34,
    height: 34,
    padding: 0,
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,

    "& svg": {
      fontSize: 18
    }
  },

  editButton: {
    color: "#0066FF"
  },

  deleteButton: {
    color: "#EF4444"
  },

  integrationFooter: {
    marginTop: theme.spacing(1.2),
    paddingTop: theme.spacing(1.1),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },

  integrationId: {
    color: theme.palette.text.secondary,
    fontSize: ".65rem",
    fontWeight: 700
  },

  integrationStatus: {
    height: 22,
    borderRadius: 999,
    fontSize: ".64rem",
    fontWeight: 800,
    background: "rgba(34,197,94,.10)",
    color: "#16A34A",
    border: "1px solid rgba(34,197,94,.18)"
  },

  emptyState: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: ".8rem"
  }
}));

const QueueIntegration = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [queueIntegration, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  const { getPlanCompany } = usePlans();
  const companyId = user.companyId;
  const history = useHistory();

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useIntegrations) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchIntegrations = async () => {
        try {
          const { data } = await api.get("/queueIntegration/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INTEGRATIONS", payload: data.queueIntegrations });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchIntegrations();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_INTEGRATIONS", payload: data.queueIntegration });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_INTEGRATION", payload: +data.integrationId });
      }
    };

    socket.on(`company-${companyId}-queueIntegration`, onQueueEvent);
    return () => {
      socket.off(`company-${companyId}-queueIntegration`, onQueueEvent);
    };
  }, [socket, companyId]);

  const handleOpenUserModal = () => {
    setSelectedIntegration(null);
    setUserModalOpen(true);
  };

  const handleCloseIntegrationModal = () => {
    setSelectedIntegration(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditIntegration = (integration) => {
    setSelectedIntegration(integration);
    setUserModalOpen(true);
  };

  const handleDeleteIntegration = async (integrationId) => {
    try {
      await api.delete(`/queueIntegration/${integrationId}`);
      toast.success(i18n.t("queueIntegration.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <div className={classes.pageRoot}>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("queueIntegration.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteIntegration(deletingUser.id)}
      >
        {i18n.t("queueIntegration.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <IntegrationModal
        open={userModalOpen}
        onClose={handleCloseIntegrationModal}
        aria-labelledby="form-dialog-title"
        integrationId={selectedIntegration && selectedIntegration.id}
      />

      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Grid container style={{ width: "100%" }}>
              <Grid item xs={12}>
                <Paper elevation={0} className={classes.pageHeader}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Typography className={classes.pageHeaderTitle}>{i18n.t("queueIntegration.title")}</Typography>
                      <Typography className={classes.pageHeaderSubtitle}>
                        Centralize e gerencie integrações com plataformas externas em um único painel.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} style={{ textAlign: "right" }}>
                      <Chip
                        icon={<DeviceHub style={{ color: "#2f4b7c", fontSize: 14 }} />}
                        label={`${queueIntegration.length} integrações`}
                        className={classes.headerChip}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper elevation={0} className={classes.controlsPaper}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={8} md={9}>
                      <TextField
                        fullWidth
                        placeholder={i18n.t("queueIntegration.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        variant="outlined"
                        className={classes.searchField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="secondary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleOpenUserModal}
                        className={classes.actionButton}
                        startIcon={<Add />}
                      >
                        {i18n.t("queueIntegration.buttons.add")}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </MainHeader>

          <Paper
            className={classes.mainPaper}
            variant="outlined"
            onScroll={handleScroll}
          >
            <div className={classes.desktopOnly}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" className={classes.tableHeaderCell} />
                    <TableCell align="center" className={classes.tableHeaderCell}>
                      {i18n.t("queueIntegration.table.id")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>
                      {i18n.t("queueIntegration.table.name")}
                    </TableCell>
                    <TableCell align="center" className={classes.tableHeaderCell}>
                      {i18n.t("queueIntegration.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {queueIntegration.map(integration => (
                    <TableRow
                      key={integration.id}
                      className={classes.tableRow}
                      hover
                    >
                      <TableCell>
                        {integration.type === "dialogflow" && <Avatar src={dialogflow} className={classes.avatar} />}
                        {integration.type === "n8n" && <Avatar src={n8n} className={classes.avatar} />}
                        {integration.type === "webhook" && <Avatar src={webhooks} className={classes.avatar} />}
                        {integration.type === "typebot" && <Avatar src={typebot} className={classes.avatar} />}
                        {integration.type === "flowbuilder" && <Avatar src={flowbuilder} className={classes.avatar} />}
                      </TableCell>

                      <TableCell align="center" className={classes.tableCellText}>
                        {integration.id}
                      </TableCell>

                      <TableCell align="center" className={classes.tableCellText}>
                        <strong>{integration.name}</strong>
                      </TableCell>

                      <TableCell align="center" className={classes.actionCell}>
                        <IconButton
                          size="small"
                          className={classes.actionIconButton}
                          onClick={() => handleEditIntegration(integration)}
                        >
                          <Edit />
                        </IconButton>

                        <IconButton
                          size="small"
                          className={classes.actionIconButton}
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingUser(integration);
                          }}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {loading && <TableRowSkeleton columns={4} />}
                </TableBody>
              </Table>
            </div>

            <div className={classes.mobileOnly}>
              <div className={classes.integrationCards}>
                {queueIntegration.map(integration => (
                  <div
                    key={`mobile-integration-${integration.id}`}
                    className={classes.integrationCard}
                  >
                    <div className={classes.integrationCardHeader}>
                      <div className={classes.integrationLogoWrap}>
                        {integration.type === "dialogflow" && <Avatar src={dialogflow} className={classes.mobileAvatar} />}
                        {integration.type === "n8n" && <Avatar src={n8n} className={classes.mobileAvatar} />}
                        {integration.type === "webhook" && <Avatar src={webhooks} className={classes.mobileAvatar} />}
                        {integration.type === "typebot" && <Avatar src={typebot} className={classes.mobileAvatar} />}
                        {integration.type === "flowbuilder" && <Avatar src={flowbuilder} className={classes.mobileAvatar} />}
                        {!["dialogflow", "n8n", "webhook", "typebot", "flowbuilder"].includes(integration.type) && (
                          <DeviceHub style={{ color: "#0066FF" }} />
                        )}
                      </div>

                      <div className={classes.integrationIdentity}>
                        <Typography className={classes.integrationName}>
                          {integration.name || "Integração sem nome"}
                        </Typography>

                        <Typography className={classes.integrationType}>
                          {integration.type || "integração"}
                        </Typography>
                      </div>

                      <div className={classes.integrationActions}>
                        <IconButton
                          className={`${classes.cardActionButton} ${classes.editButton}`}
                          onClick={() => handleEditIntegration(integration)}
                          aria-label={`Editar ${integration.name}`}
                        >
                          <Edit />
                        </IconButton>

                        <IconButton
                          className={`${classes.cardActionButton} ${classes.deleteButton}`}
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingUser(integration);
                          }}
                          aria-label={`Excluir ${integration.name}`}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </div>
                    </div>

                    <div className={classes.integrationFooter}>
                      <span className={classes.integrationId}>
                        ID #{integration.id}
                      </span>

                      <Chip
                        size="small"
                        label="Configurada"
                        className={classes.integrationStatus}
                      />
                    </div>
                  </div>
                ))}

                {!loading && queueIntegration.length === 0 && (
                  <div className={classes.emptyState}>
                    Nenhuma integração cadastrada.
                  </div>
                )}

                {loading && (
                  <div className={classes.emptyState}>
                    Carregando integrações...
                  </div>
                )}
              </div>
            </div>
          </Paper>
        </>
      )}
    </div>
  );
};

export default QueueIntegration;
